import { Calendar } from './styled';
import PropTypes from 'prop-types';
import minBy from 'lodash/minBy';
import moment from 'moment';
import { momentLocalizer } from 'react-big-calendar';
import { useTheme } from 'styled-components';
import React, { useState } from 'react';
import { formats, messages } from './fixtures';
import {
    getLimits,
    isAvailableDay,
    isInAgendas,
    isInAvailable,
    isInUnavailables,
    isUnavailableDay
} from '../../common';
const localizer = momentLocalizer(moment);

const CalendarWrapper = ({
    agenda = {},
    disabled,
    events = [],
    forceStep,
    loading,
    onRangeChange,
    onSelectEvent,
    onSelectSlot
}) => {

    const {
        successColor,
        textColor,
        turnBackgroundColor
    } = useTheme();

    const [currentView, setCurrentView] = useState('month');

    const agendaPropGetter = () => ({
        style: {
            fontSize: 13
        }
    });

    const dayPropGetter = currentDay => {
        const { agendas, availables, unavailables } = agenda;
        const day = moment(currentDay);
        const notAvailableDay = {
            backgroundColor: 'lightgray'
        };
        const unavailableDay = {
            backgroundColor: 'lightpink'
        };
        const style = {
            ...(isUnavailableDay(day, unavailables) ? unavailableDay : (!isAvailableDay(day, agendas, availables) && notAvailableDay))
        };
        return {
            style
        };
    };

    const eventPropGetter = event => {
        const { resource: { event: eventType, status } } = event;
        const admittedStyle = {
            backgroundColor: 'yellow',
            borderColor: textColor,
            color: textColor
        };
        const cancelledStyle = {
            backgroundColor: 'lightpink',
            borderColor: 'lightpink'
        };
        const defaultStyle = {
            backgroundColor: turnBackgroundColor,
            borderColor: turnBackgroundColor,
            borderStyle: 'solid',
            borderWidth: 1,
            fontSize: 13,
            maxWidth: 350
        };
        const displayStyle = {
            cursor: 'default',
            opacity: 0.3
        };
        const finalizedStyle = {
            backgroundColor: 'lightgray',
            borderColor: textColor,
            borderStyle: 'solid',
            borderWidth: 1,
            color: textColor
        };
        const inroomStyle = {
            backgroundColor: successColor,
            borderColor: textColor,
            color: textColor,
            fontWeight: 'bold'
        };
        const pendingStyle = {
            backgroundColor: 'orange',
            borderColor: 'orange'
        };
        const turnStyle = {
            cursor: 'pointer'
        };
        const unavailableStyle = {
            backgroundColor: 'lightpink',
            borderColor: 'hotpink'
        };
        const style = {
            ...defaultStyle,
            ...(eventType === 'turn' ? turnStyle : (eventType === 'unavailable' && unavailableStyle)),
            ...(status === 'absent' && cancelledStyle),
            ...(status === 'admitted' && admittedStyle),
            ...(status === 'cancelled' && cancelledStyle),
            ...(status === 'finalized' && finalizedStyle),
            ...(status === 'inroom' && inroomStyle),
            ...(status === 'pending' && pendingStyle),
            ...(eventType === 'display' && displayStyle)
        };
        return { style };
    };

    const handleRangeChange = (range, view) => {
        const newView = view ?? currentView;
        if (['agenda', 'month'].includes(newView)) {
            const { end, start } = range;
            onRangeChange(moment(start), moment(end));
        } else if (['day', 'week'].includes(newView)) {
            onRangeChange(moment(range[0]), moment(range[range.length - 1]));
        }
        if (currentView !== newView) {
            setCurrentView(newView);
        }
    };

    const slotPropGetter = slotDate => {
        const { agendas, availables, unavailables } = agenda;
        const date = moment(slotDate);
        const isInAgenda = isInAgendas(date, agendas);
        const isAvailable = isInAvailable(date, availables);
        const isUnavailables = isInUnavailables(date, unavailables);
        const backgroundColor = isUnavailables ? 'lightpink' : isAvailable ? 'lightgreen' : isInAgenda ? 'white' : 'lightgray';
        return {
            style: {
                backgroundColor
            }
        };
    };
    
    const { max, min } = getLimits(agenda);
    const { end, start } = minBy(events, ({ end, start }) => moment(end).diff(moment(start)), 'minutes') || {};
    const step = end && start ? moment(end).diff(moment(start), 'minutes') : 15;
    const filteredEvents = currentView !== 'agenda' ? events : events.filter(({ resource: { event } }) => event === 'turn');
    
    return (
        <Calendar
            dayPropGetter={ dayPropGetter }
            defaultView="month"
            eventPropGetter={ currentView !== 'agenda' ? eventPropGetter : agendaPropGetter }
            events={ filteredEvents }
            formats={ formats }
            height="100%"
            loading={ loading }
            localizer={ localizer }
            max={ max }
            messages={ messages }
            min={ min }
            selectable={ !disabled }
            slotPropGetter={ slotPropGetter }
            step={ forceStep ?? (!step || step < 5 ? 5 : step > 15 ? 15 : step) }
            view={ currentView }
            views={ ['month', 'week', 'day', 'agenda'] }
            onRangeChange={ onRangeChange && !disabled ? handleRangeChange : () => null }
            onSelectEvent={ onSelectEvent && !disabled ? onSelectEvent : () => null }
            onSelectSlot={ onSelectSlot && !disabled ? onSelectSlot : () => null }
            onView={ !disabled ? setCurrentView : () => null }
        />
    );
};

CalendarWrapper.propTypes = {
    agenda: PropTypes.object,
    disabled: PropTypes.bool,
    events: PropTypes.array,
    forceStep: PropTypes.number,
    loading: PropTypes.bool,
    onRangeChange: PropTypes.func,
    onSelectEvent: PropTypes.func,
    onSelectSlot: PropTypes.func
};

export default CalendarWrapper;
